/**
 * @fileoverview Helpers for the Details card directors row.
 *
 * Picks current and former directors out of the job CRM contact list so the
 * overview card can render names without re-implementing role filtering.
 */

import type { Id } from "@cvx/_generated/dataModel";
import type { ContactRole } from "@/lib/contact-roles";

export interface DirectorContactSummary {
  id: Id<"jobContacts">;
  displayName: string;
  /** True when the contact is a former director and not a current one. */
  isFormer: boolean;
}

interface DirectorSourceContact {
  _id: Id<"jobContacts">;
  displayName: string;
  status: string;
  roles?: ReadonlyArray<ContactRole>;
}

/**
 * Active job contacts that currently hold, or previously held, a director
 * role. Current directors sort first, then former, then alphabetically.
 */
export function selectDirectorContacts(
  contacts: ReadonlyArray<DirectorSourceContact>,
): Array<DirectorContactSummary> {
  return contacts
    .filter((contact) => contact.status === "active")
    .flatMap((contact) => {
      const roles = contact.roles ?? [];
      const isDirector = roles.includes("director");
      const isFormer = roles.includes("former_director");
      if (!isDirector && !isFormer) return [];
      return [
        {
          id: contact._id,
          displayName: contact.displayName,
          isFormer: isFormer && !isDirector,
        },
      ];
    })
    .sort((a, b) => {
      if (a.isFormer !== b.isFormer) return a.isFormer ? 1 : -1;
      return a.displayName.localeCompare(b.displayName, "en");
    });
}
